import React, { useEffect, useMemo } from 'react';
import { useFeatureFlag } from '@sparknz/feature-flags';
import {
  ErrorBoundary,
  GenericErrorScreen,
  LoginModalProvider,
} from '@sparknz/journeys-common';
import { SnackbarProvider } from '@sparknz/spark-pattern-library';
import {
  ConfigProviderProps,
  setEnvConfig,
  SparkComponentLibrary,
  UniversalLoginProvider,
} from '@sparknz/universal-login-library';

import { HeaderContentQuery$data } from '__generated__/HeaderContentQuery.graphql';
import { ProductDetailsQuery$data } from '__generated__/ProductDetailsQuery.graphql';
import { LEGACY_BUSINESS_SHOP_MOBILE_PHONES_ROUTE } from 'shared/constants';
import { useSegment } from 'shared/hooks';
import GlobalNavsLayout from 'shared/layouts/GlobalNavsLayout';
import { environmentVariables } from 'shared/utils/environment.utils';
import { isBrowser } from 'shared/utils/ssr.utils';
import AuthorableContentsProvider from '../../shared/providers/AuthorableContentsProvider/AuthorableContentsProvider';
import ProductConfigurator from './components/ProductConfigurator';
import ProductNotFoundErrorMessage from './components/ProductNotFoundErrorMessage';
import { ProductDetailsProvider, useProductDetails } from './provider';
import { ProductDetailsActionTypes } from './reducer/productDetails.reducer';
import { trackProdViewActionError } from './utils';
import { getInitialDeviceState } from './utils/device.utils';

interface ProductDetailsProps {
  product: ProductDetailsQuery$data;
  groupId?: string;
  subCategoryId?: string;
  headerContent?: HeaderContentQuery$data;
}

export function ProductDetailsInternal({
  product,
  groupId,
  subCategoryId,
  headerContent,
}: ProductDetailsProps) {
  const { dispatch } = useProductDetails();
  const { isBusiness, isPersonal } = useSegment();
  const shouldShowProductDetails = isPersonal || isBusiness;

  useEffect(() => {
    if (!shouldShowProductDetails) {
      window.location.assign(LEGACY_BUSINESS_SHOP_MOBILE_PHONES_ROUTE);
    }
  }, [shouldShowProductDetails]);

  const handleLogout = () => {
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: null,
        eligibility: null,
        selectedAccountNumber: null,
        selectedLineNumber: null,
      },
    });
    dispatch({
      type: ProductDetailsActionTypes.SetWearableEligibility,
      wearableEligibility: {
        activeStep: null,
        eligibility: null,
        selectedAccountNumber: null,
        selectedLineNumber: null,
      },
    });
  };

  if (!shouldShowProductDetails) {
    return null;
  }

  return (
    <GlobalNavsLayout ssr onLogout={handleLogout} headerContent={headerContent}>
      <ErrorBoundary
        fallback={() => <GenericErrorScreen />}
        onError={trackProdViewActionError}
      >
        {groupId ? (
          <AuthorableContentsProvider value={product.pageContent}>
            <ProductConfigurator
              product={product}
              subCategoryId={subCategoryId}
            />
          </AuthorableContentsProvider>
        ) : (
          <ProductNotFoundErrorMessage />
        )}
      </ErrorBoundary>
    </GlobalNavsLayout>
  );
}

export default function ProductDetails({
  product,
  ...props
}: ProductDetailsProps) {
  const { apiURL, hCaptchaKey } = environmentVariables;
  const config: ConfigProviderProps = {
    apiBaseURL: apiURL,
    host: '',
    brand: 'Spark',
    fallbackURL: isBrowser() ? window.location.origin : '',
    trackingScriptURL: '',
    hCaptchaKey,
    name: 'shop',
  };

  const initialDeviceState = useMemo(
    () => getInitialDeviceState(product?.productData?.offerDetails),
    [product],
  );

  const { value } = useFeatureFlag('release-enable-forgerock-login-modal');

  useEffect(() => {
    setEnvConfig(apiURL);
  }, []);

  return (
    <SnackbarProvider>
      <UniversalLoginProvider
        config={config}
        componentLibrary={SparkComponentLibrary}
      >
        <LoginModalProvider useForgerock={value}>
          <ProductDetailsProvider deviceState={initialDeviceState}>
            <ProductDetailsInternal {...props} product={product} />
          </ProductDetailsProvider>
        </LoginModalProvider>
      </UniversalLoginProvider>
    </SnackbarProvider>
  );
}
